import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/setting-in-policy.svg";
import WhatsAppButton from "../components/WhatsappButton";

const ReceivingPolicy: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          {/* Decorative SVGs */}
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[280px] w-full opacity-100"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[350px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("receivingPolicy.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-natural-800 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("receivingPolicy.hero.subtitle")}
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-2xl shadow-xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-6 leading-tight">
                {t("receivingPolicy.intro.title")}
              </h2>
              <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                {t("receivingPolicy.intro.text1")}
              </p>
              <p className="text-lg text-neutral-800 leading-relaxed">
                {t("receivingPolicy.intro.text2")}
              </p>

              <div className="bg-amber-50 rounded-2xl p-8 border border-amber-100 mt-6">
                <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                  {t("receivingPolicy.intro.supportTitle")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("receivingPolicy.intro.supportText")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Warm Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("receivingPolicy.warmIntro.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("receivingPolicy.warmIntro.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {t("receivingPolicy.warmIntro.items").map(
              (step: any, index: number) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
                >
                  <div className="text-center min-h-48">
                    <div className="text-5xl mb-4">{step.icon}</div>
                    <h3 className="text-2xl font-semibold text-neutral-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-neutral-800 leading-relaxed my-2">
                      {step.description}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-6">
                    <ul className="space-y-2 w-fit mx-auto my-auto">
                      {step.features.map((feature: string, idx: number) => (
                        <li key={idx} className="flex items-start space-x-2 text-left">
                          <div className="w-2 h-2 bg-[#492A19] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-neutral-800">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Adjustment Phases */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("receivingPolicy.phases.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("receivingPolicy.phases.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {t("receivingPolicy.phases.items").map(
              (phase: any, index: number) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 text-center"
                >
                  <div className="text-4xl mb-4">{phase.icon}</div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                    {phase.day}
                  </h3>
                  <div className="inline-block bg-[#492A19] text-white px-3 py-1 rounded-full text-sm font-semibold mb-3">
                    {phase.duration}
                  </div>
                  <p className="text-neutral-800 text-md mb-3">{phase.description}</p>
                  <p className="text-neutral-800 text-md font-medium">{phase.activities}</p>
                </div>
              )
            )}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-800 max-w-4xl mx-auto">
              {t("receivingPolicy.phases.note")}
            </p>
          </div>
        </div>

        {/* Mentor Roles */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("receivingPolicy.mentor.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("receivingPolicy.mentor.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {t("receivingPolicy.mentor.roles").map(
                (role: any, index: number) => (
                  <div
                    key={index}
                    className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex items-center justify-center space-x-4 mb-4">
                      <div className="text-3xl">{role.icon}</div>
                      <h3 className="text-xl font-semibold text-neutral-900">
                        {role.title}
                      </h3>
                    </div>
                    <p className="text-neutral-800 leading-relaxed">
                      {role.description}
                    </p>
                  </div>
                )
              )}
            </div>
            <div className="bg-[#492A19] rounded-3xl shadow-2xl p-8 text-white">
              <h3 className="text-2xl font-semibold text-center mb-6">
                {t("receivingPolicy.mentor.activitiesTitle")}
              </h3>
              <div className="grid md:grid-cols-5 gap-4">
                {t("receivingPolicy.mentor.activities").map(
                  (activity: string, index: number) => (
                    <div
                      key={index}
                      className="bg-[#FBEACD] bg-opacity-10 rounded-xl p-4 text-center backdrop-blur-sm"
                    >
                      <p className="text-sm">{activity}</p>
                    </div>
                  )
                )}
              </div>
              <p className="text-center mt-6 text-lg opacity-90">
                {t("receivingPolicy.mentor.note")}
              </p>
            </div>
          </div>
        </div>

        {/* Parents Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("receivingPolicy.parents.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("receivingPolicy.parents.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {t("receivingPolicy.parents.items").map(
              (partnership: any, index: number) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <div className="text-3xl">{partnership.icon}</div>
                    <h3 className="text-xl font-semibold text-neutral-900">
                      {partnership.title}
                    </h3>
                  </div>
                  <p className="text-neutral-800 leading-relaxed">
                    {partnership.description}
                  </p>
                </div>
              )
            )}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-neutral-800 max-w-4xl mx-auto">
              {t("receivingPolicy.parents.note")}
            </p>
          </div>
        </div>

        {/* Aftercare Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4">
              {t("receivingPolicy.aftercare.title")}
            </h2>
            <p className="text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("receivingPolicy.aftercare.subtitle")}
            </p>
          </div>
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {t("receivingPolicy.aftercare.items").map(
                (step: any, index: number) => (
                  <div
                    key={index}
                    className="bg-[#FBEACD] rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="flex items-center justify-center space-x-4 mb-4">
                      <div className="text-3xl">{step.icon}</div>
                      <h3 className="text-xl font-semibold text-neutral-900">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-neutral-800 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                )
              )}
            </div>
            <div className="text-center mt-8">
              <p className="text-lg text-neutral-800 max-w-4xl mx-auto">
                {t("receivingPolicy.aftercare.note")}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {/* <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-neutral-900 mb-4">
                {t("receivingPolicy.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("receivingPolicy.cta.subtitle")}
              </p>
              <button
                className="bg-[#FBEACD] text-[#492A19] px-8 py-3 rounded-full font-semibold text-md sm:text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("receivingPolicy.cta.button")}
              </button>
            </div>
          </div>
        </div> */}
      </div>

        {/* WhatsApp Floating Contact Button */}
        <WhatsAppButton/>
      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default ReceivingPolicy;
